import React, { useMemo } from "react"
import PropTypes from "prop-types"
import { Formik, Form } from "formik"
import * as yup from "yup"
import { useSelector } from "react-redux"
import { Modal, ModalBody } from "reactstrap"
import { Bounce, toast } from "react-toastify"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import CustomInput from "../../../components/OnboardingComponents/CustomInput"
import PreferredCitySelect from "../../../components/Common/PreferredCitySelect"
import { post } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import {
  getCityAdminCitySelectOptions,
  isCityAdminUser,
} from "../../../helpers/cityPortalRoles"
import { formatDisplayName } from "../../../helpers/userDisplay"
import { reportFormApiError } from "../../../helpers/formFieldErrors"

const TOAST_OPTIONS = {
  position: "bottom-center",
  theme: "colored",
  transition: Bounce,
}

const INITIAL_VALUES = {
  firstName: "",
  lastName: "",
  email: "",
  preferredCity: "",
  message: "",
}

const validationSchema = yup.object().shape({
  firstName: yup.string().trim().required("First name is required"),
  lastName: yup.string().trim().required("Last name is required"),
  email: yup
    .string()
    .trim()
    .email("Enter a valid email address")
    .required("Email address is required"),
  preferredCity: yup.string().required("Preferred city is required"),
  message: yup
    .string()
    .trim()
    .max(2000, "Message must be 2000 characters or fewer"),
})

/**
 * Admin Add New Member modal — POST /api/v1/admin/invites
 * City admins only see their assigned city/cities in Preferred City.
 * Platform admins load cities via paginated PreferredCitySelect (loadFromApi).
 */
const AddMemberModal = ({ isOpen, onClose, onSuccess, defaultCityId }) => {
  const currentUser = useSelector(state => state.auth.currentUser)
  const isCityAdmin = isCityAdminUser(currentUser)
  const cityAdminOptions = useMemo(
    () => getCityAdminCitySelectOptions(currentUser),
    [currentUser],
  )

  const lockedCityId = defaultCityId
    ? String(defaultCityId)
    : isCityAdmin && cityAdminOptions.length === 1
      ? cityAdminOptions[0].value
      : ""

  const staticCityOptions = useMemo(() => {
    if (!isCityAdmin && !lockedCityId) return []
    if (isCityAdmin) {
      const options = defaultCityId
        ? cityAdminOptions.filter(
            option => String(option.value) === String(defaultCityId),
          )
        : cityAdminOptions
      return options.length > 0 ? options : cityAdminOptions
    }
    const locked = cityAdminOptions.find(
      option => String(option.value) === String(lockedCityId),
    )
    return locked
      ? [locked]
      : [{ value: lockedCityId, label: "Selected city" }]
  }, [cityAdminOptions, defaultCityId, isCityAdmin, lockedCityId])

  const useApiCities = !isCityAdmin && !lockedCityId

  const handleSubmit = async (
    values,
    { setSubmitting, resetForm, setFieldError, setFieldTouched },
  ) => {
    try {
      const payload = {
        email: values.email.trim(),
        first_name: formatDisplayName(values.firstName.trim()),
        last_name: formatDisplayName(values.lastName.trim()),
        city_id: values.preferredCity,
      }
      if (values.message?.trim()) payload.message = values.message.trim()

      const response = await post(
        endpoints.createAdminMemberInvite,
        payload,
        { skipErrorToast: true },
      )
      if (response?.data?.success) {
        toast.success(
          response.data.message || "Invite sent successfully.",
          TOAST_OPTIONS,
        )
        resetForm()
        onSuccess?.()
        onClose()
      } else {
        reportFormApiError(response, "Failed to send invite", {
          setFieldError,
          setFieldTouched,
        }, {
          city_id: "preferredCity",
          first_name: "firstName",
          last_name: "lastName",
        })
      }
    } catch (err) {
      reportFormApiError(err, "Failed to send invite", {
        setFieldError,
        setFieldTouched,
      }, {
        city_id: "preferredCity",
        first_name: "firstName",
        last_name: "lastName",
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={onClose}
      centered
      className="amok-modal amok-modal--password"
      contentClassName="amok-modal__content"
      backdropClassName="amok-modal__backdrop amok-modal-backdrop--blur"
    >
      <ModalBody className="amok-modal__body edit-profile__modal-body">
        <Formik
          key={isOpen ? "add-member-open" : "add-member-closed"}
          initialValues={{
            ...INITIAL_VALUES,
            preferredCity: lockedCityId || "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            setFieldTouched,
            isSubmitting,
          }) => (
            <Form className="edit-profile__modal-form">
              <div className="edit-profile__modal-main">
                <h3 className="edit-profile__modal-title">Add New Member</h3>
                <div className="edit-profile__modal-fields">
                  <CustomInput
                    label="First Name"
                    name="firstName"
                    required
                    placeholder="Enter first name"
                    value={values.firstName}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched("firstName", true)}
                    error={touched.firstName && errors.firstName}
                    autoComplete="given-name"
                  />
                  <CustomInput
                    label="Last Name"
                    name="lastName"
                    required
                    placeholder="Enter last name"
                    value={values.lastName}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched("lastName", true)}
                    error={touched.lastName && errors.lastName}
                    autoComplete="family-name"
                  />
                  <CustomInput
                    label="Email Address"
                    type="email"
                    name="email"
                    required
                    placeholder="Enter email address"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched("email", true)}
                    error={touched.email && errors.email}
                    autoComplete="email"
                  />
                  <PreferredCitySelect
                    label="Preferred City"
                    required
                    loadFromApi={useApiCities}
                    placeholder="Select a city"
                    cityOptions={staticCityOptions}
                    value={values.preferredCity}
                    onChange={value => {
                      if (lockedCityId) return
                      setFieldValue("preferredCity", value)
                    }}
                    onBlur={() => setFieldTouched("preferredCity", true)}
                    error={touched.preferredCity && errors.preferredCity}
                    isDisabled={Boolean(lockedCityId)}
                  />
                  <CustomInput
                    label="Message"
                    name="message"
                    placeholder="Optional invite message"
                    value={values.message}
                    onChange={handleChange}
                    onBlur={() => setFieldTouched("message", true)}
                    error={touched.message && errors.message}
                    textarea
                  />
                </div>
              </div>
              <div className="edit-profile__modal-actions">
                <CustomButton
                  variant="danger"
                  size="md"
                  type="button"
                  className="edit-profile__modal-cancel"
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </CustomButton>
                <CustomButton
                  variant="primary"
                  size="md"
                  type="submit"
                  className="edit-profile__modal-submit"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  Send Invite
                </CustomButton>
              </div>
            </Form>
          )}
        </Formik>
      </ModalBody>
    </Modal>
  )
}

AddMemberModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  defaultCityId: PropTypes.string,
}

AddMemberModal.defaultProps = {
  onSuccess: undefined,
  defaultCityId: undefined,
}

export default AddMemberModal
